import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, payload } from "./lib/api";
import { uploadMedia } from "./lib/upload-media";

export type PageSection = { ref: string; label: string };
export type BlockKind = "text" | "textImage" | "gallery" | "cta" | "columns" | "faq";
export type BlockItem = { title: string; text: string; image: string };
export type ContentBlock = {
  id: string; pagePath: string; anchorRef: string; anchorLabel: string;
  kind: BlockKind; style: "plain" | "card" | "accent"; imageSide: "left" | "right";
  title: string; text: string; image: string; images: string[]; items: BlockItem[];
  buttonLabel: string; buttonUrl: string; draft?: boolean;
};
export type BuilderRequest = { nonce: number } & (
  | { kind: "add"; anchorRef?: string; anchorLabel?: string; afterBlockId?: string }
  | { kind: "block"; id: string; action: "edit" | "up" | "down" | "duplicate" | "delete" }
);

const KINDS: Array<{ kind: BlockKind; name: string; hint: string; icon: ReactNode }> = [
  { kind: "text", name: "Titolo e testo", hint: "Un titolo e un paragrafo, con pulsante se vuoi.", icon: <svg viewBox="0 0 64 44"><rect x="14" y="8" width="36" height="5" rx="2"/><rect x="8" y="20" width="48" height="3" rx="1.5" opacity=".5"/><rect x="8" y="27" width="48" height="3" rx="1.5" opacity=".5"/><rect x="8" y="34" width="30" height="3" rx="1.5" opacity=".5"/></svg> },
  { kind: "textImage", name: "Foto e testo", hint: "Foto da un lato, testo dall'altro.", icon: <svg viewBox="0 0 64 44"><rect x="6" y="6" width="26" height="32" rx="3"/><rect x="38" y="10" width="20" height="5" rx="2"/><rect x="38" y="20" width="20" height="3" rx="1.5" opacity=".5"/><rect x="38" y="27" width="20" height="3" rx="1.5" opacity=".5"/></svg> },
  { kind: "gallery", name: "Galleria foto", hint: "Da 2 a 8 foto in griglia.", icon: <svg viewBox="0 0 64 44"><rect x="5" y="6" width="17" height="14" rx="2"/><rect x="24" y="6" width="17" height="14" rx="2"/><rect x="43" y="6" width="17" height="14" rx="2"/><rect x="5" y="24" width="17" height="14" rx="2"/><rect x="24" y="24" width="17" height="14" rx="2"/><rect x="43" y="24" width="17" height="14" rx="2"/></svg> },
  { kind: "columns", name: "Colonne con card", hint: "2-4 riquadri affiancati: servizi, vantaggi…", icon: <svg viewBox="0 0 64 44"><rect x="5" y="8" width="16" height="28" rx="3"/><rect x="24" y="8" width="16" height="28" rx="3"/><rect x="43" y="8" width="16" height="28" rx="3"/></svg> },
  { kind: "cta", name: "Invito all'azione", hint: "Un messaggio con un grande pulsante.", icon: <svg viewBox="0 0 64 44"><rect x="12" y="8" width="40" height="5" rx="2"/><rect x="18" y="18" width="28" height="3" rx="1.5" opacity=".5"/><rect x="18" y="26" width="28" height="11" rx="5.5"/></svg> },
  { kind: "faq", name: "Domande e risposte", hint: "Domande frequenti che si aprono al tocco.", icon: <svg viewBox="0 0 64 44"><rect x="8" y="6" width="48" height="9" rx="3"/><rect x="8" y="18" width="48" height="9" rx="3" opacity=".6"/><rect x="8" y="30" width="48" height="9" rx="3" opacity=".6"/></svg> },
];
const KIND_NAME = Object.fromEntries(KINDS.map((item) => [item.kind, item.name])) as Record<BlockKind, string>;

const newId = () => `b${crypto.randomUUID().replace(/-/g, "").slice(0, 16)}`;
const emptyItem = (): BlockItem => ({ title: "", text: "", image: "" });

function normPath(value: string) {
  let path = value || "/";
  if (path.endsWith("/index.html")) path = path.slice(0, -10);
  else if (path.endsWith(".html")) path = path.slice(0, -5);
  while (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path || "/";
}

function blank(kind: BlockKind, pagePath: string, anchorRef: string, anchorLabel: string): ContentBlock {
  const base: ContentBlock = {
    id: newId(), pagePath, anchorRef, anchorLabel, kind, style: "plain", imageSide: "right",
    title: "", text: "", image: "", images: [], items: [], buttonLabel: "", buttonUrl: "",
  };
  if (kind === "columns") base.items = [emptyItem(), emptyItem(), emptyItem()];
  if (kind === "faq") base.items = [emptyItem(), emptyItem()];
  if (kind === "cta") base.style = "accent";
  return base;
}

function hasContent(block: ContentBlock) {
  return Boolean(block.title.trim() || block.text.trim() || block.image || block.images.length || block.items.some((item) => item.title.trim() || item.text.trim() || item.image) || (block.buttonLabel.trim() && block.buttonUrl.trim()));
}

function clean(block: ContentBlock): ContentBlock {
  const { draft: _draft, ...rest } = block;
  void _draft;
  return { ...rest, items: rest.items.filter((item) => item.title.trim() || item.text.trim() || item.image) };
}

export default function SectionBuilder({ siteId, canEdit, currentPath, sections, request, onPreview, onActiveChange }: {
  siteId: string; canEdit: boolean; currentPath: string; sections: PageSection[];
  request: BuilderRequest | null;
  onPreview: (blocks: ContentBlock[], focusId?: string) => void;
  onActiveChange: (active: boolean) => void;
}) {
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);
  const [view, setView] = useState<"idle" | "library" | "form">("idle");
  const [target, setTarget] = useState({ anchorRef: "end:", anchorLabel: "In fondo alla pagina", afterBlockId: "" });
  const [draft, setDraft] = useState<ContentBlock | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const rootRef = useRef<HTMLElement>(null);
  const here = normPath(currentPath);
  const usable = useMemo(() => sections.filter((item) => item.label !== "Footer"), [sections]);
  const pageBlocks = useMemo(() => blocks.filter((block) => normPath(block.pagePath) === here), [blocks, here]);

  useEffect(() => {
    let cancelled = false;
    api<{ blocks: ContentBlock[] }>(`/api/sites/${siteId}/content-blocks`)
      .then((result) => { if (!cancelled) setBlocks(result.blocks.map((block) => ({ ...block, images: block.images || [], items: block.items || [], kind: block.kind || "text", imageSide: block.imageSide || "right" }))); })
      .catch(() => { if (!cancelled) setBlocks([]); });
    return () => { cancelled = true; };
  }, [siteId]);

  useEffect(() => { onActiveChange(view !== "idle"); }, [view, onActiveChange]);

  const insertIndex = useCallback((list: ContentBlock[], anchorRef: string, afterBlockId: string) => {
    if (afterBlockId) {
      const index = list.findIndex((block) => block.id === afterBlockId);
      if (index >= 0) return index + 1;
    }
    if (anchorRef === "end:") return list.length;
    const first = list.findIndex((block) => block.anchorRef === anchorRef && normPath(block.pagePath) === here);
    return first >= 0 ? first : list.length;
  }, [here]);

  const withDraft = useCallback((list: ContentBlock[], value: ContentBlock, fresh: boolean, afterBlockId: string) => {
    const marked = { ...value, draft: true };
    if (!fresh) return list.map((block) => (block.id === value.id ? marked : block));
    const next = [...list];
    next.splice(insertIndex(list, value.anchorRef, afterBlockId), 0, marked);
    return next;
  }, [insertIndex]);

  async function persist(next: ContentBlock[], done: string, focusId?: string) {
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await api<{ blocks: ContentBlock[] }>(`/api/sites/${siteId}/content-blocks`, payload("PUT", { blocks: next.map(clean) }));
      const saved = result.blocks.map((block) => ({ ...block, images: block.images || [], items: block.items || [] }));
      setBlocks(saved);
      onPreview(saved, focusId);
      setMessage(done);
      setView("idle"); setDraft(null);
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Salvataggio non riuscito");
      return false;
    } finally { setBusy(false); }
  }

  function openLibrary(anchorRef?: string, anchorLabel?: string, afterBlockId?: string) {
    setError(""); setMessage("");
    setTarget({ anchorRef: anchorRef || "end:", anchorLabel: anchorLabel || (anchorRef && anchorRef !== "end:" ? anchorRef : "In fondo alla pagina"), afterBlockId: afterBlockId || "" });
    setDraft(null); setView("library");
    onPreview(blocks);
    window.setTimeout(() => rootRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }), 40);
  }

  function pickKind(kind: BlockKind) {
    const fresh = blank(kind, currentPath, target.anchorRef, target.anchorLabel);
    setDraft(fresh); setIsNew(true); setView("form");
    onPreview(withDraft(blocks, fresh, true, target.afterBlockId), fresh.id);
  }

  function edit(block: ContentBlock) {
    setError(""); setMessage("");
    setDraft({ ...block }); setIsNew(false); setView("form");
    onPreview(withDraft(blocks, block, false, ""), block.id);
    window.setTimeout(() => rootRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }), 40);
  }

  function change(patch: Partial<ContentBlock>) {
    if (!draft) return;
    const next = { ...draft, ...patch };
    setDraft(next);
    onPreview(withDraft(blocks, next, isNew, target.afterBlockId), next.id);
  }

  function cancel() {
    setView("idle"); setDraft(null); setError("");
    onPreview(blocks);
  }

  async function publish() {
    if (!draft) return;
    if (!hasContent(draft)) { setError("Aggiungi almeno un titolo, un testo o una foto prima di pubblicare."); return; }
    const value = clean(draft);
    const next = isNew
      ? (() => { const list = [...blocks]; list.splice(insertIndex(blocks, value.anchorRef, target.afterBlockId), 0, value); return list; })()
      : blocks.map((block) => (block.id === value.id ? value : block));
    await persist(next, isNew ? "Sezione pubblicata sul sito." : "Modifiche pubblicate.", value.id);
  }

  function neighbours(block: ContentBlock) {
    const same = blocks.filter((item) => normPath(item.pagePath) === here);
    const index = same.findIndex((item) => item.id === block.id);
    return { previous: same[index - 1], next: same[index + 1] };
  }

  async function move(block: ContentBlock, direction: -1 | 1) {
    const { previous, next } = neighbours(block);
    const sibling = direction === -1 ? previous : next;
    if (sibling && sibling.anchorRef === block.anchorRef) {
      const list = blocks.map((item) => (item.id === block.id ? sibling : item.id === sibling.id ? block : item));
      await persist(list, "Ordine aggiornato.", block.id);
      return;
    }
    // cambia sezione di riferimento: salta alla sezione precedente/successiva
    const refs = [...usable.map((item) => item.ref), "end:"];
    const labelOf = (ref: string) => (ref === "end:" ? "In fondo alla pagina" : usable.find((item) => item.ref === ref)?.label || "");
    const at = refs.indexOf(block.anchorRef);
    const to = at < 0 ? -1 : at + direction;
    if (to < 0 || to >= refs.length) { setMessage(direction === -1 ? "È già la prima sezione." : "È già l'ultima sezione."); return; }
    const moved = { ...block, anchorRef: refs[to], anchorLabel: labelOf(refs[to]) };
    const rest = blocks.filter((item) => item.id !== block.id);
    const targetBlocks = rest.filter((item) => item.anchorRef === moved.anchorRef && normPath(item.pagePath) === here);
    let list: ContentBlock[];
    if (direction === -1 && targetBlocks.length) {
      const lastIndex = rest.findIndex((item) => item.id === targetBlocks[targetBlocks.length - 1].id);
      list = [...rest.slice(0, lastIndex + 1), moved, ...rest.slice(lastIndex + 1)];
    } else if (direction === 1 && targetBlocks.length) {
      const firstIndex = rest.findIndex((item) => item.id === targetBlocks[0].id);
      list = [...rest.slice(0, firstIndex), moved, ...rest.slice(firstIndex)];
    } else list = [...rest, moved];
    await persist(list, "Sezione spostata.", block.id);
  }

  async function duplicate(block: ContentBlock) {
    const copy = { ...block, id: newId(), title: block.title ? `${block.title} (copia)` : block.title };
    const list = [...blocks];
    list.splice(list.findIndex((item) => item.id === block.id) + 1, 0, copy);
    await persist(list, "Sezione duplicata.", copy.id);
  }

  async function remove(block: ContentBlock) {
    if (!window.confirm("Eliminare questa sezione dal sito?")) return;
    await persist(blocks.filter((item) => item.id !== block.id), "Sezione eliminata.");
  }

  // richieste che arrivano dall'anteprima (punti «+ Aggiungi sezione» e barra sulla sezione)
  const handled = useRef(0);
  useEffect(() => {
    if (!request || request.nonce === handled.current) return;
    handled.current = request.nonce;
    if (!canEdit) return;
    if (request.kind === "add") { openLibrary(request.anchorRef, request.anchorLabel, request.afterBlockId); return; }
    const block = blocks.find((item) => item.id === request.id);
    if (!block) return;
    if (request.action === "edit") edit(block);
    else if (request.action === "up") void move(block, -1);
    else if (request.action === "down") void move(block, 1);
    else if (request.action === "duplicate") void duplicate(block);
    else if (request.action === "delete") void remove(block);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  async function upload(file: File): Promise<string> {
    setUploading(true); setError("");
    try {
      const media = await uploadMedia(siteId, file, draft?.title || "Immagine");
      return new URL(media.url, location.origin).href;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Caricamento non riuscito");
      return "";
    } finally { setUploading(false); }
  }

  const photoField = (label: string, value: string, onChange: (url: string) => void) => (
    <div className="sb-photo"><span>{label}</span>
      {value && <img src={value} alt="" />}
      <div className="sb-photo-row">
        <label className="sb-upload">{value ? "Cambia foto" : "Carica una foto"}<input type="file" accept="image/jpeg,image/png,image/webp,image/avif" onChange={(event) => { const file = event.target.files?.[0]; if (file) void upload(file).then((url) => url && onChange(url)); event.target.value = ""; }} /></label>
        {value && <button type="button" className="sb-link" onClick={() => onChange("")}>Rimuovi</button>}
      </div>
      <input type="url" placeholder="oppure incolla l'indirizzo (https://…)" value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );

  const buttonFields = (required: boolean) => (
    <fieldset className="sb-group"><legend>{required ? "Pulsante (obbligatorio)" : "Pulsante (facoltativo)"}</legend>
      <label>Testo del pulsante<input value={draft?.buttonLabel || ""} maxLength={40} placeholder="Es. Prenota ora" onChange={(event) => change({ buttonLabel: event.target.value })} /></label>
      <label>Dove porta<input value={draft?.buttonUrl || ""} placeholder="https://… oppure tel:+39…" onChange={(event) => change({ buttonUrl: event.target.value })} /></label>
    </fieldset>
  );

  const styleField = (
    <label>Aspetto<select value={draft?.style} onChange={(event) => change({ style: event.target.value as ContentBlock["style"] })}>
      <option value="plain">Semplice</option><option value="card">Riquadro con bordo</option><option value="accent">Riquadro evidenziato</option>
    </select></label>
  );

  function updateItem(index: number, patch: Partial<BlockItem>) {
    if (!draft) return;
    change({ items: draft.items.map((item, i) => (i === index ? { ...item, ...patch } : item)) });
  }

  function renderForm(d: ContentBlock) {
    return <>
      {d.kind === "text" && <>
        <label>Titolo<input value={d.title} maxLength={140} onChange={(event) => change({ title: event.target.value })} /></label>
        <label>Testo<textarea rows={5} value={d.text} maxLength={2000} onChange={(event) => change({ text: event.target.value })} /></label>
        {photoField("Foto (facoltativa)", d.image, (url) => change({ image: url }))}
        {styleField}
        {buttonFields(false)}
      </>}
      {d.kind === "textImage" && <>
        {photoField("Foto", d.image, (url) => change({ image: url }))}
        <div className="sb-seg" role="group" aria-label="Posizione della foto">
          <button type="button" className={d.imageSide === "left" ? "on" : ""} onClick={() => change({ imageSide: "left" })}>Foto a sinistra</button>
          <button type="button" className={d.imageSide === "right" ? "on" : ""} onClick={() => change({ imageSide: "right" })}>Foto a destra</button>
        </div>
        <label>Titolo<input value={d.title} maxLength={140} onChange={(event) => change({ title: event.target.value })} /></label>
        <label>Testo<textarea rows={5} value={d.text} maxLength={2000} onChange={(event) => change({ text: event.target.value })} /></label>
        {buttonFields(false)}
      </>}
      {d.kind === "gallery" && <>
        <label>Titolo (facoltativo)<input value={d.title} maxLength={140} onChange={(event) => change({ title: event.target.value })} /></label>
        <div className="sb-gallery">
          {d.images.map((url, index) => <div key={url + index} className="sb-thumb"><img src={url} alt="" /><button type="button" aria-label="Rimuovi foto" onClick={() => change({ images: d.images.filter((_, i) => i !== index) })}>×</button></div>)}
          {d.images.length < 8 && <label className="sb-add-photo">＋ Aggiungi foto<input type="file" multiple accept="image/jpeg,image/png,image/webp,image/avif" onChange={async (event) => {
            const files = [...(event.target.files || [])].slice(0, 8 - d.images.length);
            event.target.value = "";
            const urls: string[] = [];
            for (const file of files) { const url = await upload(file); if (url) urls.push(url); }
            if (urls.length) change({ images: [...d.images, ...urls] });
          }} /></label>}
        </div>
        <p className="sb-hint">{d.images.length}/8 foto. Le foto si dispongono da sole in griglia.</p>
      </>}
      {d.kind === "columns" && <>
        <label>Titolo della sezione (facoltativo)<input value={d.title} maxLength={140} onChange={(event) => change({ title: event.target.value })} /></label>
        {d.items.map((item, index) => <fieldset className="sb-group" key={index}><legend>Card {index + 1}{d.items.length > 2 && <button type="button" className="sb-link" onClick={() => change({ items: d.items.filter((_, i) => i !== index) })}>Rimuovi</button>}</legend>
          {photoField("Foto (facoltativa)", item.image, (url) => updateItem(index, { image: url }))}
          <label>Titolo<input value={item.title} maxLength={160} onChange={(event) => updateItem(index, { title: event.target.value })} /></label>
          <label>Testo<textarea rows={3} value={item.text} maxLength={1200} onChange={(event) => updateItem(index, { text: event.target.value })} /></label>
        </fieldset>)}
        {d.items.length < 4 && <button type="button" className="sb-secondary" onClick={() => change({ items: [...d.items, emptyItem()] })}>＋ Aggiungi una card</button>}
        {buttonFields(false)}
      </>}
      {d.kind === "cta" && <>
        <label>Titolo<input value={d.title} maxLength={140} placeholder="Es. Vieni a trovarci" onChange={(event) => change({ title: event.target.value })} /></label>
        <label>Testo (facoltativo)<textarea rows={3} value={d.text} maxLength={2000} onChange={(event) => change({ text: event.target.value })} /></label>
        {styleField}
        {buttonFields(true)}
      </>}
      {d.kind === "faq" && <>
        <label>Titolo<input value={d.title} maxLength={140} placeholder="Es. Domande frequenti" onChange={(event) => change({ title: event.target.value })} /></label>
        {d.items.map((item, index) => <fieldset className="sb-group" key={index}><legend>Domanda {index + 1}{d.items.length > 1 && <button type="button" className="sb-link" onClick={() => change({ items: d.items.filter((_, i) => i !== index) })}>Rimuovi</button>}</legend>
          <label>Domanda<input value={item.title} maxLength={160} onChange={(event) => updateItem(index, { title: event.target.value })} /></label>
          <label>Risposta<textarea rows={3} value={item.text} maxLength={1200} onChange={(event) => updateItem(index, { text: event.target.value })} /></label>
        </fieldset>)}
        {d.items.length < 12 && <button type="button" className="sb-secondary" onClick={() => change({ items: [...d.items, emptyItem()] })}>＋ Aggiungi una domanda</button>}
      </>}
    </>;
  }

  const anchorOptions = [...usable, { ref: "end:", label: "In fondo alla pagina" }];

  return <section className="sb-panel" ref={rootRef}>
    {view === "idle" && <>
      <button type="button" className="sb-add" disabled={!canEdit} onClick={() => openLibrary()}>
        <b>＋</b><span><strong>Aggiungi sezione</strong><small>Scegli un modello: testo, foto, galleria, card, domande…</small></span>
      </button>
      <p className="sb-tip">Nell'anteprima passa il mouse tra due sezioni: compare <em>＋ Aggiungi sezione</em>, proprio nel punto che vuoi.</p>
      {message && <div className="notice success">{message}</div>}
      {error && <div className="notice error">{error}</div>}
      {pageBlocks.length > 0 && <div className="sb-list"><h4>Sezioni aggiunte a questa pagina</h4>
        {pageBlocks.map((block) => <div className="sb-item" key={block.id}>
          <div><strong>{block.title || block.text.slice(0, 40) || KIND_NAME[block.kind]}</strong><small>{KIND_NAME[block.kind]} · dopo «{block.anchorLabel || "fondo pagina"}»</small></div>
          <div className="sb-item-actions">
            <button type="button" aria-label="Sposta su" disabled={!canEdit || busy} onClick={() => void move(block, -1)}>↑</button>
            <button type="button" aria-label="Sposta giù" disabled={!canEdit || busy} onClick={() => void move(block, 1)}>↓</button>
            <button type="button" disabled={!canEdit || busy} onClick={() => edit(block)}>Modifica</button>
            <button type="button" disabled={!canEdit || busy} onClick={() => void duplicate(block)}>Duplica</button>
            <button type="button" className="danger" disabled={!canEdit || busy} onClick={() => void remove(block)}>Elimina</button>
          </div>
        </div>)}
      </div>}
    </>}

    {view === "library" && <>
      <div className="sb-head"><button type="button" className="sb-back" onClick={cancel}>← Indietro</button><h3>Aggiungi sezione</h3></div>
      <label className="sb-where">Dove la inserisco?
        <select value={target.anchorRef} onChange={(event) => { const option = anchorOptions.find((item) => item.ref === event.target.value); setTarget({ anchorRef: event.target.value, anchorLabel: option?.label || "", afterBlockId: "" }); }}>
          {!anchorOptions.some((item) => item.ref === target.anchorRef) && <option value={target.anchorRef}>{target.anchorLabel}</option>}
          {anchorOptions.map((item) => <option key={item.ref} value={item.ref}>{item.ref === "end:" ? item.label : `Dopo: ${item.label}`}</option>)}
        </select>
      </label>
      <div className="sb-library">
        {KINDS.map((item) => <button type="button" key={item.kind} className="sb-kind" onClick={() => pickKind(item.kind)}>
          <span className="sb-icon">{item.icon}</span><strong>{item.name}</strong><small>{item.hint}</small>
        </button>)}
      </div>
    </>}

    {view === "form" && draft && <>
      <div className="sb-head"><button type="button" className="sb-back" onClick={() => (isNew ? setView("library") : cancel())}>← {isNew ? "Cambia modello" : "Indietro"}</button><h3>{isNew ? "Nuova sezione" : "Modifica sezione"}: {KIND_NAME[draft.kind]}</h3></div>
      <p className="sb-hint">Quello che scrivi si vede subito nell'anteprima, con il contorno tratteggiato. Online solo quando premi «Pubblica».</p>
      {error && <div className="notice error">{error}</div>}
      <fieldset className="sb-form" disabled={!canEdit || busy}>{renderForm(draft)}</fieldset>
      <div className="sb-foot">
        <button type="button" className="sb-publish" disabled={!canEdit || busy || uploading} onClick={() => void publish()}>{busy ? "Pubblico…" : uploading ? "Carico la foto…" : "Pubblica sul sito"}</button>
        <button type="button" onClick={cancel} disabled={busy}>Annulla</button>
        {!isNew && <button type="button" className="danger" disabled={!canEdit || busy} onClick={() => void remove(draft)}>Elimina</button>}
      </div>
    </>}
  </section>;
}
